import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { PRODUCT_META } from "@/data/products";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { Checkbox } from "@/components/ui/checkbox";
import { Minus, Plus, X, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { isChristmasSeasonActive } from "@/lib/christmas";

function formatNZD(amount) {
  return new Intl.NumberFormat("en-NZ", {
    style: "currency",
    currency: "NZD",
  }).format(amount);
}

export default function CartDrawer() {
  const {
    items,
    isOpen,
    setIsOpen,
    increment,
    decrement,
    removeItem,
    setPersonalize,
    setPersonalizeNote,
    setRecipientAge,
    addItem,
    count,
  } = useCart();

  const [catalog, setCatalog] = useState({});
  const [shippingRates, setShippingRates] = useState({ urban: 0, rural: 0 });
  const [personalizationFee, setPersonalizationFee] = useState(0);
  const [personalizationEligible, setPersonalizationEligible] = useState([]);
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/products");
        const map = {};
        (res.data.products || []).forEach((p) => (map[p.id] = p));
        setCatalog(map);
        setShippingRates(
          res.data.shipping_rates || { urban: 8.99, rural: 14.99 }
        );
        setPersonalizationFee(Number(res.data.personalization_fee || 0));
        setPersonalizationEligible(res.data.personalization_eligible || []);
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  const enriched = items
    .map((i) => {
      const p = catalog[i.product_id];
      if (!p) return null;
      const meta = PRODUCT_META[i.product_id];
      const eligible = personalizationEligible.includes(i.product_id);
      const lineTotal = p.amount * i.quantity;
      const linePersonalization =
        eligible && i.personalize ? personalizationFee * i.quantity : 0;
      return {
        ...i,
        product: p,
        meta,
        eligible,
        lineTotal,
        linePersonalization,
      };
    })
    .filter(Boolean);

  const subtotal = enriched.reduce((sum, i) => sum + i.lineTotal, 0);
  const personalizationTotal = enriched.reduce(
    (sum, i) => sum + i.linePersonalization,
    0
  );
  // Auto-bundle: for every Butterfly+Stocking pair in the cart, save $2
  // (mirrors backend logic so preview + Stripe agree).
  const bundlePairs = Math.min(
    enriched.find((r) => r.product_id === "butterfly_collection")?.quantity ||
      0,
    enriched.find((r) => r.product_id === "christmas_stocking")?.quantity || 0
  );
  const bundleDiscount = bundlePairs * 2;
  const hasItems = enriched.length > 0;
  const total = Math.max(0, subtotal + personalizationTotal - bundleDiscount);

  const missingAge = enriched.some((i) => !i.recipient_age);
  const soldOutInCart = enriched.some((i) => i.product?.in_stock === false);

  const handleCheckout = async () => {
    if (!hasItems) return;
    if (missingAge) {
      toast.error("Please choose the recipient's age for each box.");
      return;
    }
    try {
      setCheckoutLoading(true);
      const res = await api.post("/checkout/session", {
        items: items.map((i) => ({
          product_id: i.product_id,
          quantity: i.quantity,
          personalize: !!i.personalize,
          personalize_note: i.personalize_note || null,
          recipient_age: i.recipient_age || null,
        })),
        origin_url: window.location.origin,
      });
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        throw new Error("No checkout URL");
      }
    } catch (e) {
      console.error(e);
      toast.error("Couldn't start checkout. Please try again.");
      setCheckoutLoading(false);
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-md bg-[#FFFDF9] border-l border-[#E8D7D0] p-0 flex flex-col"
        data-testid="cart-drawer"
      >
        <SheetHeader className="px-6 py-5 border-b border-[#E8D7D0] text-left">
          <SheetTitle className="font-serif-me text-2xl text-[#36292A]">
            Your basket
          </SheetTitle>
          <SheetDescription className="sr-only">
            Your current basket. Review items, choose recipient ages, and
            continue to secure Stripe checkout.
          </SheetDescription>
          <p className="text-xs tracking-[0.24em] uppercase text-[#7D6B6D]">
            {count} {count === 1 ? "item" : "items"}
          </p>
        </SheetHeader>

        <div className="flex-1 overflow-y-auto px-6 py-6">
          {!hasItems && (
            <div
              className="flex flex-col items-center justify-center h-full text-center"
              data-testid="cart-empty"
            >
              <div className="w-16 h-16 rounded-full bg-[#F9F6F0] flex items-center justify-center">
                <span className="font-serif-me italic text-2xl text-[#C48B9F]">
                  ø
                </span>
              </div>
              <div className="mt-6 font-serif-me text-2xl text-[#36292A]">
                Your basket is empty.
              </div>
              <p className="mt-3 text-sm text-[#7D6B6D] max-w-xs">
                Add one of our boxes and it&apos;ll appear here, ready to send
                a little love.
              </p>
              <button
                className="me-btn-primary mt-8"
                onClick={() => setIsOpen(false)}
                data-testid="cart-continue-shopping"
              >
                Continue shopping
              </button>
            </div>
          )}

          {hasItems && (
            <ul className="space-y-6">
              {enriched.map((row) => (
                <li
                  key={row.product_id}
                  className="pb-6 border-b border-[#E8D7D0] last:border-b-0 last:pb-0"
                  data-testid={`cart-item-${row.product_id}`}
                >
                  <div className="flex gap-4">
                    <div className="w-20 h-24 rounded-xl overflow-hidden bg-[#F9F6F0] flex-shrink-0 border border-[#E8D7D0]">
                      {row.meta?.image ? (
                        <img
                          src={row.meta.image}
                          alt={row.meta.imageAlt}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center font-serif-me text-[#C48B9F]">
                          Me
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <div className="font-serif-me text-lg text-[#36292A] leading-tight">
                            {row.meta?.title || row.product.name}
                          </div>
                          <div className="text-[10px] tracking-[0.28em] uppercase text-[#7D6B6D] mt-1">
                            {row.meta?.tagline || row.product.type}
                          </div>
                        </div>
                        <button
                          aria-label="Remove"
                          className="text-[#7D6B6D] hover:text-[#36292A] mt-1"
                          onClick={() => removeItem(row.product_id)}
                          data-testid={`cart-remove-${row.product_id}`}
                        >
                          <X size={16} />
                        </button>
                      </div>
                      <div className="mt-3 flex items-center justify-between">
                        <div className="inline-flex items-center border border-[#E8D7D0] rounded-full">
                          <button
                            aria-label="Decrease quantity"
                            className="w-8 h-8 flex items-center justify-center text-[#36292A] hover:text-[#C48B9F]"
                            onClick={() => decrement(row.product_id)}
                            data-testid={`cart-decrement-${row.product_id}`}
                          >
                            <Minus size={14} />
                          </button>
                          <span
                            className="w-8 text-center text-sm text-[#36292A]"
                            data-testid={`cart-quantity-${row.product_id}`}
                          >
                            {row.quantity}
                          </span>
                          <button
                            aria-label="Increase quantity"
                            className="w-8 h-8 flex items-center justify-center text-[#36292A] hover:text-[#C48B9F]"
                            onClick={() => increment(row.product_id)}
                            data-testid={`cart-increment-${row.product_id}`}
                          >
                            <Plus size={14} />
                          </button>
                        </div>
                        <div
                          className="text-sm text-[#36292A]"
                          data-testid={`cart-line-total-${row.product_id}`}
                        >
                          {formatNZD(row.lineTotal)}
                        </div>
                      </div>

                      <div className="mt-3 flex items-center gap-3">
                        <label className="text-[10px] tracking-[0.24em] uppercase text-[#7D6B6D] whitespace-nowrap">
                          Recipient age
                        </label>
                        <select
                          value={row.recipient_age ?? ""}
                          onChange={(e) =>
                            setRecipientAge(row.product_id, e.target.value)
                          }
                          className={
                            "flex-1 rounded-full border bg-white text-sm py-1.5 px-3 text-[#36292A] focus:outline-none focus:border-[#C48B9F] " +
                            (row.recipient_age
                              ? "border-[#E8D7D0]"
                              : "border-[#C48B9F]")
                          }
                          data-testid={`cart-age-${row.product_id}`}
                        >
                          <option value="">Choose age…</option>
                          {Array.from({ length: 12 }, (_, i) => i + 4).map(
                            (age) => (
                              <option key={age} value={age}>
                                {age} years old
                              </option>
                            )
                          )}
                        </select>
                      </div>
                    </div>
                  </div>

                  {row.eligible && (
                    <div
                      className="mt-4 rounded-2xl bg-[#F9F6F0] border border-[#E8D7D0] p-4"
                      data-testid={`personalize-block-${row.product_id}`}
                    >
                      <label className="flex items-start gap-3 cursor-pointer">
                        <Checkbox
                          checked={!!row.personalize}
                          onCheckedChange={(v) =>
                            setPersonalize(row.product_id, !!v)
                          }
                          className="mt-1 border-[#C48B9F] data-[state=checked]:bg-[#C48B9F] data-[state=checked]:border-[#C48B9F]"
                          data-testid={`personalize-toggle-${row.product_id}`}
                        />
                        <div className="flex-1">
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-sm font-medium text-[#36292A]">
                              Personalise for the birthday girl
                            </span>
                            <span className="text-xs tracking-[0.2em] uppercase text-[#C48B9F]">
                              +{formatNZD(personalizationFee)}
                              {row.quantity > 1 ? ` × ${row.quantity}` : ""}
                            </span>
                          </div>
                          <p className="mt-1 text-xs text-[#7D6B6D] leading-relaxed">
                            Add her name, a message on the card, or a note about her favourite colours.
                          </p>
                        </div>
                      </label>

                      {row.personalize && (
                        <div className="mt-4">
                          <label className="text-[10px] tracking-[0.28em] uppercase text-[#7D6B6D]">
                            Personalisation details
                          </label>
                          <textarea
                            value={row.personalize_note || ""}
                            onChange={(e) =>
                              setPersonalizeNote(
                                row.product_id,
                                e.target.value
                              )
                            }
                            placeholder="e.g. Isla's 8th birthday — she loves lavender and unicorns. Please write 'Happy Birthday Isla, love Mum'."
                            rows={3}
                            maxLength={280}
                            className="mt-1 w-full rounded-lg border border-[#E8D7D0] bg-white px-3 py-2 text-sm text-[#36292A] focus:outline-none focus:border-[#C48B9F] resize-none"
                            data-testid={`personalize-note-${row.product_id}`}
                          />
                          <div className="mt-1 text-[10px] tracking-[0.2em] uppercase text-[#7D6B6D] text-right">
                            {(row.personalize_note || "").length}/280
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          )}

          {hasItems &&
            catalog.christmas_stocking &&
            isChristmasSeasonActive() &&
            !items.some(
              (i) =>
                i.product_id === "christmas_stocking" ||
                i.product_id === "butterfly_stocking_bundle"
            ) && (
              <div
                className="mt-6 relative overflow-hidden rounded-2xl border border-[#F1D9DA]"
                style={{
                  background:
                    "linear-gradient(115deg, #FDF3EE 0%, #F8E3E5 60%, #F1D9DA 100%)",
                }}
                data-testid="cart-upsell-christmas_stocking"
              >
                <div className="flex gap-4 p-4">
                  <div className="w-20 h-24 rounded-xl overflow-hidden bg-white/60 flex-shrink-0 border border-white/70">
                    {PRODUCT_META.christmas_stocking?.image && (
                      <img
                        src={PRODUCT_META.christmas_stocking.image}
                        alt={PRODUCT_META.christmas_stocking.imageAlt}
                        className="w-full h-full object-cover"
                      />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5 text-[9px] tracking-[0.3em] uppercase text-[#B23A48]">
                      <Sparkles size={11} strokeWidth={2.2} />
                      Holiday add-on
                    </div>
                    <div className="mt-1 font-serif-me text-lg text-[#36292A] leading-tight">
                      Add a Stocking Stuffer for just{" "}
                      {formatNZD(catalog.christmas_stocking.amount)}
                    </div>
                    <p className="mt-1 text-[11px] text-[#7D6B6D] leading-relaxed">
                      Notebook & pen, keyring, mirror, affirmations card &amp;
                      four scrunchies — a little extra magic on the tree.
                    </p>
                    <button
                      type="button"
                      className="mt-3 inline-flex items-center gap-1.5 rounded-full bg-[#B23A48] text-white text-xs font-medium tracking-wide px-4 py-2 hover:bg-[#932F3C] transition-colors"
                      onClick={() => {
                        addItem("christmas_stocking", 1);
                        toast.success(
                          "Christmas Stocking Stuffer added to your basket."
                        );
                      }}
                      data-testid="cart-upsell-add-christmas_stocking"
                    >
                      Add to basket
                      <span aria-hidden>+</span>
                    </button>
                  </div>
                </div>
              </div>
            )}
        </div>

        {hasItems && (
          <div className="border-t border-[#E8D7D0] px-6 py-5 bg-[#F9F6F0]">
            <div className="space-y-2 text-sm text-[#36292A]">
              <div className="flex justify-between">
                <span>Subtotal</span>
                <span data-testid="cart-subtotal">{formatNZD(subtotal)}</span>
              </div>
              {personalizationTotal > 0 && (
                <div className="flex justify-between">
                  <span>Personalisation</span>
                  <span data-testid="cart-personalization">
                    {formatNZD(personalizationTotal)}
                  </span>
                </div>
              )}
              {bundleDiscount > 0 && (
                <div
                  className="flex justify-between items-center text-[#8E4A7B]"
                  data-testid="cart-bundle-discount"
                >
                  <span className="inline-flex items-center gap-1.5">
                    <span aria-hidden>✦</span>
                    Bundle saving applied
                    {bundlePairs > 1 ? ` × ${bundlePairs}` : ""}
                  </span>
                  <span>−{formatNZD(bundleDiscount)}</span>
                </div>
              )}
              <div
                className="flex justify-between text-[#7D6B6D]"
                data-testid="cart-delivery-note"
              >
                <span>Shipping</span>
                <span>
                  {formatNZD(Number(shippingRates.urban || 0))} –{" "}
                  {formatNZD(Number(shippingRates.rural || 0))}
                </span>
              </div>
              <div className="flex justify-between text-base pt-3 border-t border-[#E8D7D0] mt-2 font-serif-me">
                <span>Total</span>
                <span data-testid="cart-total">{formatNZD(total)}</span>
              </div>
              <p className="text-[10px] tracking-[0.24em] uppercase text-[#7D6B6D] text-right pt-1">
                Shipping (Urban / Rural NZ) chosen at checkout
              </p>
            </div>
            <button
              className="me-btn-primary w-full justify-center mt-5"
              onClick={handleCheckout}
              disabled={checkoutLoading || missingAge || soldOutInCart}
              data-testid="cart-checkout"
            >
              {checkoutLoading ? "Redirecting…" : "Checkout"}
              <span aria-hidden>→</span>
            </button>
            {soldOutInCart && (
              <p
                className="mt-2 text-[11px] text-[#B23A48] text-center"
                data-testid="cart-soldout-warning"
              >
                Remove sold-out items to continue.
              </p>
            )}
            {!soldOutInCart && missingAge && (
              <p className="mt-2 text-[11px] text-[#C48B9F] text-center">
                Choose the recipient&apos;s age to continue.
              </p>
            )}
            <button
              className="me-btn-ghost w-full justify-center mt-3"
              onClick={() => setIsOpen(false)}
              data-testid="cart-continue-shopping-footer"
            >
              Continue shopping
            </button>
            <p className="mt-3 text-[10px] tracking-[0.24em] uppercase text-[#7D6B6D] text-center">
              Secure payment via Stripe
            </p>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
